// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import OrderDetailPageTypeLink from "@insite/content-library/Components/OrderDetailPageTypeLink";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import DataTableHeader from "@insite/mobius/DataTable/DataTableHeader";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Pagination from "@insite/mobius/Pagination";
import TextField from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import TableCounter, { TableCounterStyles } from "@steiner/App/Components/Common/TableCounter";
import XngLoadingOverlay from "@steiner/App/Components/Common/XngLoadingOverlay";
import Notify from "@steiner/App/Components/Notify";
import loadOpenBackOrders from "@steiner/App/Handlers/XNG/OpenBackOrders/LoadOpenBackOrders";
import sendOpenBackOrdersByEmail from "@steiner/App/Handlers/XNG/OpenBackOrders/SendOpenBackOrdersByEmail";
import subscribeToOpenBackOrders from "@steiner/App/Handlers/XNG/OpenBackOrders/Subscribe";
import unsubscribeToOpenBackOrders from "@steiner/App/Handlers/XNG/OpenBackOrders/Unsubscribe";
import { OpenBackOrderEmailProductModel, OpenBackOrdersApiParameter } from "@steiner/App/Services/OpenBackOrders";
import { CustomAppState } from "@steiner/App/Store/Reducers";
import { xIsAuthenticated } from "@steiner/App/Store/Selectors/SessionSelectors";
import DataTable from "@steiner/MobiusOverrides/DataTable";
import DataTableBody from "@steiner/MobiusOverrides/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@steiner/MobiusOverrides/DataTable/DataTableCell";
import DataTableHead from "@steiner/MobiusOverrides/DataTable/DataTableHead";
import { DataTableHeaderPresentationProps } from "@steiner/MobiusOverrides/DataTable/DataTableHeader";
import DataTableRow from "@steiner/MobiusOverrides/DataTable/DataTableRow";
import { PaginationPresentationProps } from "@steiner/MobiusOverrides/Pagination";
import { TextFieldPresentationProps } from "@steiner/MobiusOverrides/TextField";
import { XNGAGE_ELEMENTS } from "@steiner/XDL/constants";
import { xValidateOnSubmit } from "@steiner/XDL/Utilities";
import { openPrintDialog } from "@steiner/XDL/Utilities/print";
import React, { useContext, useEffect, useState } from "react";
import { FC } from "react";
import { connect, ResolveThunks } from "react-redux";

export interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: CustomAppState, props: OwnProps) => {
    return {
        orders: state.xOpenBackOrders.openBackOrders,
        pagination: state.xOpenBackOrders.pagination,
        getOpenBackOrdersParameter: state.xOpenBackOrders.getOpenBackOrdersParameter,
        isLoading: state.xOpenBackOrders.isLoading,
        customerNumber: getBillToState(state, getSession(state).billToId).value?.customerNumber,
        isAuthenticated: xIsAuthenticated(state),
        signInPageUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        openBackOrdersPageUrl: getPageLinkByPageType(state, "OpenBackOrdersPage")?.url,
        subscribedToOpenBackOrders: state.xOpenBackOrders.isSubscribed,
    };
};

const mapDispatchToProps = {
    loadOpenBackOrders,
    subscribeToOpenBackOrders,
    unsubscribeToOpenBackOrders,
    sendOpenBackOrdersByEmail,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface OpenBackOrdersStyles {
    container?: InjectableCss;
    notifyMessage?: TypographyPresentationProps;
    descriptionHeaderCell?: DataTableHeaderPresentationProps;
    descriptionCell?: DataTableCellProps;
    actionContainer?: InjectableCss;
    exportLink?: LinkPresentationProps;
    printLink?: LinkPresentationProps;
    pagination?: PaginationPresentationProps;
    tableCounterStyles?: TableCounterStyles;
    loadingStyle?: InjectableCss;
    unSubscribeLink?: LinkPresentationProps;
    subscribeLink?: LinkPresentationProps;
    upperSection?: InjectableCss;
    sendSheetAsEmailFormContainer?: InjectableCss;
    emailTextField?: TextFieldPresentationProps;
    emailButton?: ButtonPresentationProps;
}

export const openBackOrdersStyles: OpenBackOrdersStyles = {};

const styles = openBackOrdersStyles;

const TableHeaderActions: FC<{
    customerNumber?: string;
    getOpenBackOrdersParameter?: OpenBackOrdersApiParameter;
}> = ({ customerNumber, getOpenBackOrdersParameter }) => {
    return (
        <StyledWrapper {...styles.actionContainer}>
            <Link
                {...styles.exportLink}
                target="_blank"
                href={`/api/str/openbackorderbyproducts/exportopenbackordertofile?fileType=csv&customerNumber=${customerNumber}&shipToAddress=${getOpenBackOrdersParameter?.shipToAddress}&orderNo=${getOpenBackOrdersParameter?.orderNumber}&releaseNumber=${getOpenBackOrdersParameter?.releaseNumber}&orderedBy=${getOpenBackOrdersParameter?.orderedBy}&partNumber=${getOpenBackOrdersParameter?.partNumber}&description=${getOpenBackOrdersParameter?.description}`}
            >
                {translate("CSV")}
            </Link>
            <Link
                {...styles.exportLink}
                target="_blank"
                href={`/api/str/openbackorderbyproducts/exportopenbackordertofile?fileType=txt&customerNumber=${customerNumber}&shipToAddress=${getOpenBackOrdersParameter?.shipToAddress}&orderNo=${getOpenBackOrdersParameter?.orderNumber}&releaseNumber=${getOpenBackOrdersParameter?.releaseNumber}&orderedBy=${getOpenBackOrdersParameter?.orderedBy}&partNumber=${getOpenBackOrdersParameter?.partNumber}&description=${getOpenBackOrdersParameter?.description}`}
            >
                {translate("Text")}
            </Link>
            <Link {...styles.printLink} onClick={openPrintDialog} data-test-selector="openBackOrders_Print">
                {translate("Print")}
            </Link>
        </StyledWrapper>
    );
};

const formId = "OpenBackOrdersEmailForm";

export const OpenBackOrders: FC<Props> = ({
    orders,
    isLoading,
    pagination,
    customerNumber,
    getOpenBackOrdersParameter,
    isAuthenticated,
    history,
    signInPageUrl,
    openBackOrdersPageUrl,
    subscribedToOpenBackOrders,
    loadOpenBackOrders,
    subscribeToOpenBackOrders,
    unsubscribeToOpenBackOrders,
    sendOpenBackOrdersByEmail,
}) => {
    const toasterContext = useContext(ToasterContext);
    const [subscribed, setSubscribed] = useState<boolean>(subscribedToOpenBackOrders || false);
    const [email, setEmail] = useState("");
    const [listOfProducts, setListOfProducts] = useState<OpenBackOrderEmailProductModel[]>([]);

    useEffect(() => {
        setSubscribed(subscribedToOpenBackOrders || false);
    }, [subscribedToOpenBackOrders]);

    const subscribe = () => {
        subscribeToOpenBackOrders({
            onSuccess: () => {
                setSubscribed(true);
                toasterContext.addToast({
                    body: siteMessage("STR_SubscribedToOpenBackOrderByProductsEmail_Successfully"),
                    messageType: "success",
                });
            },
            onError: () => {
                setSubscribed(false);
                toasterContext.addToast({
                    body: siteMessage("STR_OpenBackOrders_Subscribe_Error_msg"),
                    messageType: "danger",
                });
            },
        });
    };

    const unSubscribe = () => {
        unsubscribeToOpenBackOrders({
            onSuccess: () => {
                setSubscribed(false);
                toasterContext.addToast({
                    body: siteMessage("STR_UnsubscribedFromOpenBackOrderByProductsEmail_Successfully"),
                    messageType: "success",
                });
            },
            onError: () => {
                toasterContext.addToast({
                    body: siteMessage("STR_OpenBackOrders_UnSubscribe_Error_msg"),
                    messageType: "danger",
                });
            },
        });
    };

    const changePage = (newPageIndex: number) => {
        loadOpenBackOrders({
            ...getOpenBackOrdersParameter,
            page: newPageIndex,
            pageSize: pagination?.pageSize,
        });
    };

    const changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = parseInt(event.currentTarget.value, 10);
        loadOpenBackOrders({
            ...getOpenBackOrdersParameter,
            page: 1,
            pageSize: newPageSize,
        });
    };

    const handleSendEmailWithSheet = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const errors = xValidateOnSubmit(formId);
        if (errors?.length > 0) {
            return;
        }

        sendOpenBackOrdersByEmail({
            email,
            onSuccess: () => {
                toasterContext.addToast({
                    body: siteMessage("STR_OpenBackOrders_Email_Sent_Successfully").toString().replace("{0}", email),
                    messageType: "success",
                });
                setEmail("");
            },
            onError: () => {
                toasterContext.addToast({
                    body: siteMessage("STR_OpenBackOrders_Sending_Email_Failed"),
                    messageType: "danger",
                });
            },
        });
    };

    if (!isAuthenticated) {
        history.push(`${signInPageUrl}?returnUrl=${openBackOrdersPageUrl}`);
        return null;
    }

    if (!orders) {
        return null;
    }

    return (
        <XngLoadingOverlay
            data-test-selector="openBackOrders_Loading"
            id="openBackOrdersLoading"
            loading={isLoading || false}
            {...styles.loadingStyle}
        >
            <StyledWrapper {...styles.container}>
                {orders.length > 0 ? (
                    <>
                        <StyledWrapper {...styles.upperSection}>
                            <StyledWrapper {...styles.sendSheetAsEmailFormContainer}>
                                <form onSubmit={handleSendEmailWithSheet}>
                                    <TextField
                                        placeholder={translate("Email")}
                                        value={email}
                                        type="email"
                                        onChange={e => setEmail(e.target.value)}
                                        required
                                        {...{ formId, messageLabel: translate("Email") }}
                                        {...styles.emailTextField}
                                    />
                                    <Button type="submit" {...styles.emailButton}>
                                        {translate("Email")}
                                    </Button>
                                </form>
                            </StyledWrapper>
                            {subscribed ? (
                                <Link onClick={unSubscribe} {...styles.unSubscribeLink}>
                                    {translate("Unsubscribe")}
                                </Link>
                            ) : (
                                <Link onClick={subscribe} {...styles.subscribeLink}>
                                    {translate("Subscribe")}
                                </Link>
                            )}
                        </StyledWrapper>
                        <TableCounter
                            extendedStyles={styles.tableCounterStyles}
                            pagination={pagination}
                            countText={translate("Products")}
                            accordionOnSmallScreen={false}
                            actions={
                                <TableHeaderActions
                                    customerNumber={customerNumber}
                                    getOpenBackOrdersParameter={getOpenBackOrdersParameter}
                                />
                            }
                        >
                            <DataTable>
                                <DataTableHead>
                                    <DataTableHeader title={translate("Qty")}>{translate("Qty")}</DataTableHeader>
                                    <DataTableHeader title={translate("Part Number")}>
                                        {translate("Part Number")}
                                    </DataTableHeader>
                                    <DataTableHeader title={translate("Description")} {...styles.descriptionHeaderCell}>
                                        {translate("Description")}
                                    </DataTableHeader>
                                    <DataTableHeader title={translate("Order Date")}>
                                        {translate("Order Date")}
                                    </DataTableHeader>
                                    <DataTableHeader title={translate("Order #")}>
                                        {translate("Order #")}
                                    </DataTableHeader>
                                    <DataTableHeader title={translate("CustomerPo")}>
                                        {translate("CustomerPo")}
                                    </DataTableHeader>
                                    <DataTableHeader title={translate("Ship Date")}>
                                        {translate("Ship Date")}
                                    </DataTableHeader>
                                    <DataTableHeader title={translate("Required Date")}>
                                        {translate("Required Date")}
                                    </DataTableHeader>
                                    <DataTableHeader title={translate("Customer Part Number")}>
                                        {translate("Customer Part Number")}
                                    </DataTableHeader>
                                    <DataTableHeader title={translate("Order Status")}>
                                        {translate("Order Status")}
                                    </DataTableHeader>
                                    <DataTableHeader title={translate("Ship Via")}>
                                        {translate("Ship Via")}
                                    </DataTableHeader>
                                    <DataTableHeader title={translate("Release Number")}>
                                        {translate("Release Number")}
                                    </DataTableHeader>
                                    <DataTableHeader title={translate("Ordered By")}>
                                        {translate("Ordered By")}
                                    </DataTableHeader>
                                </DataTableHead>
                                <DataTableBody>
                                    {orders.map(order => (
                                        <DataTableRow key={order.orderId}>
                                            <DataTableCell {...{ header: translate("Qty", "Qty") }}>
                                                {order.qty}
                                            </DataTableCell>
                                            <DataTableCell {...{ header: translate("Part Number", "partNumber") }}>
                                                {order.partNumber}
                                            </DataTableCell>
                                            <DataTableCell
                                                {...styles.descriptionCell}
                                                {...{ header: translate("Description", "description") }}
                                            >
                                                {order.productUri ? (
                                                    <Link href={order.productUri}>{order.description}</Link>
                                                ) : (
                                                    order.description
                                                )}
                                            </DataTableCell>
                                            <DataTableCell {...{ header: translate("Order Date", "orderDate") }}>
                                                {order.orderDate}
                                            </DataTableCell>
                                            <DataTableCell {...{ header: translate("Order #", "orderId") }}>
                                                <OrderDetailPageTypeLink
                                                    title={order.orderId}
                                                    orderNumber={order.orderId}
                                                    isVmiOrder={false}
                                                    isVmiOrderDetailsPage={false}
                                                />
                                            </DataTableCell>
                                            <DataTableCell {...{ header: translate("CustomerPo", "customerPo") }}>
                                                {order.customerPo}
                                            </DataTableCell>
                                            <DataTableCell {...{ header: translate("Ship Date", "shipDate") }}>
                                                {order.shipDate}
                                            </DataTableCell>
                                            <DataTableCell {...{ header: translate("Required Date", "requiredDate") }}>
                                                {order.requiredDate}
                                            </DataTableCell>
                                            <DataTableCell
                                                {...{ header: translate("Customer Part Number", "customerPartNumber") }}
                                            >
                                                {order.customerPartNumber}
                                            </DataTableCell>
                                            <DataTableCell {...{ header: translate("Order Status", "orderStatus") }}>
                                                {order.orderStatus}
                                            </DataTableCell>
                                            <DataTableCell {...{ header: translate("Ship Via", "shipVia") }}>
                                                {order.shipVia}
                                            </DataTableCell>
                                            <DataTableCell
                                                {...{ header: translate("Release Number", "releaseNumber") }}
                                            >
                                                {order.releaseNumber}
                                            </DataTableCell>
                                            <DataTableCell {...{ header: translate("Ordered By", "orderedBy") }}>
                                                {order.orderedBy}
                                            </DataTableCell>
                                        </DataTableRow>
                                    ))}
                                </DataTableBody>
                            </DataTable>
                        </TableCounter>
                        {pagination && pagination.totalItemCount > pagination.pageSizeOptions[0] && (
                            <Pagination
                                {...styles.pagination}
                                resultsCount={pagination.totalItemCount}
                                currentPage={pagination.page}
                                resultsPerPage={pagination.pageSize}
                                resultsPerPageOptions={pagination.pageSizeOptions}
                                onChangePage={changePage}
                                onChangeResultsPerPage={changeResultsPerPage}
                                pageSizeCookie="openBackOrders-PageSize"
                                data-test-selector="openBackOrders_pagination"
                                prevPage={pagination.prevPageUri}
                                nextPage={pagination.nextPageUri}
                            />
                        )}
                    </>
                ) : (
                    !isLoading && (
                        <Notify type="info" {...styles.notifyMessage}>
                            {translate("No orders could be found")}
                        </Notify>
                    )
                )}
            </StyledWrapper>
        </XngLoadingOverlay>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(OpenBackOrders)),
    definition: {
        group: XNGAGE_ELEMENTS,
        allowedContexts: ["OpenBackOrdersPage"],
    },
};

export default widgetModule;
